import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { selectLocale } from 'models/localization';
import fetchBoothTaxonomiesQueryRequest, {
  FetchBoothTaxonomiesProps,
} from 'network/fetchBoothTaxonomies';
import { boothTaxonomiesAdapter, selectAllBoothTaxonomies } from './selectors';
import { boothTaxonomyEntity, BoothTaxonomy } from './types';
import { fetchCachedCMSData } from '../common/thunks';

export const fetchBoothTaxonomies = createAsyncThunk(
  'boothTaxonomies/fetchBoothTaxonomies',
  async (config: FetchBoothTaxonomiesProps = {}, { getState }) => {
    try {
      const state = getState();
      const locale = selectLocale(state);
      const taxonomies = selectAllBoothTaxonomies(state);
      const { boothTaxonomies } = await fetchBoothTaxonomiesQueryRequest({ locales: [locale], ...config });
      const { entities } = normalize<BoothTaxonomy>([...taxonomies, ...boothTaxonomies], [boothTaxonomyEntity]);
      return entities.boothTaxonomies;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
  {
    condition(_, { getState }) {
      const state = getState();
      const locale = selectLocale(state);
      const taxonomies = selectAllBoothTaxonomies(state);
      return !Object.keys(taxonomies?.[0]?.name || {}).includes(locale);
    },
  },
);

export const boothTaxonomiesSlice = createSlice({
  name: 'boothTaxonomies',
  initialState: boothTaxonomiesAdapter.getInitialState({
    fetching: true,
    selectedCategoryIds: [],
  }),
  reducers: {
    setSelectedCategoryIds(state, action: PayloadAction<string[]>) {
      state.selectedCategoryIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCachedCMSData.rejected, (state) => {
      if (state.fetching) state.fetching = false;
    });
    builder.addCase(fetchCachedCMSData.fulfilled, (state, action) => {
      state.fetching = false;
      const { boothTaxonomies } = action.payload;
      if (boothTaxonomies && Object.keys(boothTaxonomies).length) {
        boothTaxonomiesAdapter.upsertMany(state, boothTaxonomies);
      }
    });

    builder.addCase(fetchBoothTaxonomies.pending, (state) => {
      state.fetching = true;
    });
    builder.addCase(fetchBoothTaxonomies.rejected, (state) => {
      if (state.fetching) state.fetching = false;
    });
    builder.addCase(fetchBoothTaxonomies.fulfilled, (state, action) => {
      state.fetching = false;
      if (action.payload && Object.keys(action.payload).length) {
        boothTaxonomiesAdapter.upsertMany(state, action.payload);
      }
    });
  },
});

export default boothTaxonomiesSlice.reducer;
export const { setSelectedCategoryIds } = boothTaxonomiesSlice.actions;
